import { CONFIG } from "@rentiohq/shared-frontend/dist/config/app.config";
import {
  ERevenuesInvoicedStatus,
  ERevenuesPaymentStatus,
} from "@rentiohq/shared-frontend/dist/reduxV2/exportFile";
import { IPagedQuery } from "@rentiohq/shared-frontend/dist/reduxV2/utils/api.types";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import { EPayoutType } from "@rentiohq/shared-frontend/dist/types/payment.types";
import { EPaymentRequestStatus } from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import {
  endOfDay,
  endOfMonth,
  endOfWeek,
  startOfDay,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { TAppearance } from "@rentiohq/web-shared/dist/types";
import mergeDeepLeft from "ramda/es/mergeDeepLeft";
import { EPaymentQuickFilters } from "./Revenues.types";

export enum ERevenueType {
  Income = "income",
  Other = "other",
  Deleted = "deleted",
}

const NOW = new Date();

export const getDateFilter = (
  paymentDateQuick?: EPaymentQuickFilters,
  paymentDateRange?: [Date | null, Date | null],
) => {
  switch (paymentDateQuick) {
    case EPaymentQuickFilters.Today:
      return {
        startedAt: {
          between: [startOfDay(NOW), endOfDay(NOW)],
        },
      };

    case EPaymentQuickFilters.ThisWeek:
      return {
        startedAt: {
          between: [startOfWeek(NOW), endOfWeek(NOW)],
        },
      };

    case EPaymentQuickFilters.ThisMonth:
      return {
        startedAt: {
          between: [startOfMonth(NOW), endOfMonth(NOW)],
        },
      };

    case EPaymentQuickFilters.PreviousMonth:
      const nowLastMonth = subMonths(NOW, 1);

      return {
        startedAt: {
          between: [startOfMonth(nowLastMonth), endOfMonth(nowLastMonth)],
        },
      };

    case EPaymentQuickFilters.DateRange: {
      const start = paymentDateRange?.[0];
      const end = paymentDateRange?.[1];

      if (start && end) {
        return {
          startedAt: {
            between: [start, end],
          },
        };
      }

      return undefined;
    }

    default:
      return undefined;
  }
};

export const getFilterQueryV2 = (params: {
  type: ERevenueType;
  query?: string;
  page: number;
  order?: { field: string; method: string };
  paymentStatus: ERevenuesPaymentStatus[];
  invoicesStatus: ERevenuesInvoicedStatus[];
  contactMeMaster: IContact;
  paymentOrderWheres?: any;
  dateRangeForPayment?: {
    [key: string]: any;
  }[];
}): IPagedQuery | undefined => {
  const {
    type,
    query,
    page,
    order,
    paymentStatus,
    invoicesStatus,
    contactMeMaster,
    paymentOrderWheres,
    dateRangeForPayment = [],
  } = params;

  if (!contactMeMaster) {
    return undefined;
  }

  let whereForType = {};
  switch (type) {
    case ERevenueType.Income:
      whereForType = {
        requestPayeeAccountId: {
          in: [
            contactMeMaster.accountId,
            // TODO: Limited because the default URL length cannot be that long without throwing a CORS error
            // The account ID's should be an alias so backend can fill it in with all account IDs
            ...contactMeMaster.accountIds.slice(0, 1700),
          ],
        },
        payoutType: { eq: EPayoutType.Payout },
      };
      break;

    case ERevenueType.Other:
      whereForType = {
        requestPayeeAccountId: {
          notIn: [
            contactMeMaster.accountId,
            // TODO: Limited because the default URL length cannot be that long without throwing a CORS error
            // The account ID's should be an alias so backend can fill it in with all account IDs
            ...contactMeMaster.accountIds.slice(0, 1700),
          ],
        },
        payoutType: { eq: EPayoutType.Payout },
      };
      break;

    case ERevenueType.Deleted:
      // TODO: Check if used
      whereForType = {
        deletedAt: { neq: null },
        payoutType: { eq: EPayoutType.Payout },
      };

      break;

    default:
      break;
  }

  let newFilter: IPagedQuery = {
    filter: whereForType,
    sort: [
      { field: "startedAt", method: "ASC" },
      { field: "id", method: "DESC" },
    ],
    limit: CONFIG.DEFAULT_FETCH_LIMIT,
    page: 1,
  };

  if (
    invoicesStatus.length < Object.values(ERevenuesInvoicedStatus).length &&
    invoicesStatus.length > 0
  ) {
    if (invoicesStatus[0] === ERevenuesInvoicedStatus.Invoiced) {
      // @ts-ignore
      newFilter = mergeDeepLeft(
        {
          filter: {
            or: [
              {
                invoicedAt: { isNot: null },
              },
              {
                externalInvoiceId: { isNot: null },
              },
            ],
          },
        },
        newFilter,
      );
    } else {
      // @ts-ignore
      newFilter = mergeDeepLeft(
        {
          filter: {
            and: [
              {
                invoicedAt: { is: null },
              },
              {
                externalInvoiceId: { is: null },
              },
            ],
          },
        },
        newFilter,
      );
    }
  }

  let statuses: EPaymentRequestStatus[] = [];
  paymentStatus.forEach((simpleStatus: ERevenuesPaymentStatus) => {
    statuses = [...statuses, ...getAdvancedStatuses(simpleStatus)];
  });

  if (statuses.length > 0) {
    newFilter = mergeDeepLeft(
      { filter: { status: { in: statuses } } },
      newFilter,
    );
  }

  if (dateRangeForPayment?.length > 0) {
    newFilter = {
      ...newFilter,
      filter: {
        ...newFilter.filter,
        and: [
          // @ts-ignore
          ...(newFilter?.filter?.and || []),
          ...dateRangeForPayment,
        ],
      },
    };
  }

  if (order) {
    // @ts-ignore
    newFilter = mergeDeepLeft(
      {
        sort: [
          { field: `${order.field}`, method: `${order.method}` },
          { field: "id", method: "DESC" },
        ],
      },
      newFilter,
    );
  }

  if (query && query?.length > 0) {
    // @ts-ignore
    newFilter = mergeDeepLeft({ search: query }, newFilter);
  }

  if (paymentOrderWheres) {
    // @ts-ignore
    newFilter = mergeDeepLeft(
      {
        filter: {
          paymentOrder: paymentOrderWheres,
        },
      },
      newFilter,
    );
  }

  if (page) {
    // @ts-ignore
    newFilter = mergeDeepLeft({ page: page || 1 }, newFilter);
  }

  return newFilter;
};

export const getFilterQuery = (params: {
  type: ERevenueType;
  query?: string;
  page: number;
  order?: { field: string; method: string };
  paymentStatus: ERevenuesPaymentStatus[];
  invoicesStatus: ERevenuesInvoicedStatus[];
  contactMeMaster: IContact;
  paymentOrderWheres?: any;
  paymentDateQuick?: EPaymentQuickFilters;
  paymentDateRange?: [Date | null, Date | null];
}) => {
  const {
    type,
    query,
    page,
    order,
    paymentStatus,
    invoicesStatus,
    contactMeMaster,
    paymentOrderWheres,
    paymentDateQuick,
    paymentDateRange,
  } = params;

  if (!contactMeMaster) {
    return undefined;
  }

  let whereForType = {};
  switch (type) {
    case ERevenueType.Income:
      whereForType = {
        requestPayeeAccountId: {
          inq: [
            contactMeMaster.accountId,
            // TODO: Limited because the default URL length cannot be that long without throwing a CORS error
            // The account ID's should be an alias so backend can fill it in with all account IDs
            ...contactMeMaster.accountIds.slice(0, 1700),
          ],
        },
        payoutType: EPayoutType.Payout,
      };
      break;

    case ERevenueType.Other:
      whereForType = {
        requestPayeeAccountId: {
          nin: [
            contactMeMaster.accountId,
            // TODO: Limited because the default URL length cannot be that long without throwing a CORS error
            // The account ID's should be an alias so backend can fill it in with all account IDs
            ...contactMeMaster.accountIds.slice(0, 1700),
          ],
        },
        payoutType: EPayoutType.Payout,
      };
      break;

    case ERevenueType.Deleted:
      // TODO: Check if used
      whereForType = {
        deletedAt: { neq: null },
        payoutType: EPayoutType.Payout,
      };

      break;

    default:
      break;
  }

  let newFilter: {
    where: any;
    include?: string[];
    order?: string;
    search?: string;
    page?: number;
    limit?: number;
    paymentOrderWhere: any;
  } = {
    where: whereForType,
    paymentOrderWhere: paymentOrderWheres,
    include: ["paymentOrder"],
    order: "startedAt ASC, id DESC",
    limit: CONFIG.DEFAULT_FETCH_LIMIT,
  };

  if (
    invoicesStatus.length < Object.values(ERevenuesInvoicedStatus).length &&
    invoicesStatus.length > 0
  ) {
    if (invoicesStatus[0] === ERevenuesInvoicedStatus.Invoiced) {
      // @ts-ignore
      newFilter = mergeDeepLeft(
        {
          where: {
            or: [
              {
                invoicedAt: { neq: null },
              },
              {
                externalInvoiceId: { neq: null },
              },
            ],
          },
        },
        newFilter,
      );
    } else {
      // @ts-ignore
      newFilter = mergeDeepLeft(
        {
          where: {
            and: [
              {
                invoicedAt: null,
              },
              {
                externalInvoiceId: null,
              },
            ],
          },
        },
        newFilter,
      );
    }
  }

  let statuses: EPaymentRequestStatus[] = [];
  paymentStatus.forEach((simpleStatus: ERevenuesPaymentStatus) => {
    statuses = [...statuses, ...getAdvancedStatuses(simpleStatus)];
  });

  if (statuses.length > 0) {
    // @ts-ignore
    newFilter = mergeDeepLeft(
      { where: { status: { inq: statuses } } },
      newFilter,
    );
  }

  const dateFilter = getDateFilter(paymentDateQuick, paymentDateRange);

  if (dateFilter) {
    newFilter = mergeDeepLeft({ where: { ...dateFilter } }, newFilter);
  }

  if (order) {
    // @ts-ignore
    newFilter = mergeDeepLeft(
      { order: `${order.field} ${order.method}, id DESC` },
      newFilter,
    );
  }

  if (query && query?.length > 0) {
    // @ts-ignore
    newFilter = mergeDeepLeft({ search: query }, newFilter);
  }

  if (page) {
    // @ts-ignore
    newFilter = mergeDeepLeft({ page: page || 1 }, newFilter);
  }

  return newFilter;
};

export const getSimpleStatus = (
  status: EPaymentRequestStatus,
): [ERevenuesPaymentStatus, TAppearance] => {
  switch (status) {
    case EPaymentRequestStatus.Paid:
      return [ERevenuesPaymentStatus.Paid, "success"];

    case EPaymentRequestStatus.New:
      return [ERevenuesPaymentStatus.Unpaid, "warning"];

    default:
      return [ERevenuesPaymentStatus.Pending, "info"];
  }
};

export const getAdvancedStatuses = (status: ERevenuesPaymentStatus) => {
  switch (status) {
    case ERevenuesPaymentStatus.Paid:
      return [EPaymentRequestStatus.Paid];

    case ERevenuesPaymentStatus.Unpaid:
      return [EPaymentRequestStatus.New];

    case ERevenuesPaymentStatus.Pending:
      return [
        EPaymentRequestStatus.Approved,
        EPaymentRequestStatus.Pending,
        EPaymentRequestStatus.PendingThirdParty,
        EPaymentRequestStatus.PayInPlanned,
        EPaymentRequestStatus.PaidIn,
        EPaymentRequestStatus.Partial,
        EPaymentRequestStatus.Transferring,
        EPaymentRequestStatus.Transferred,
        EPaymentRequestStatus.PayingOut,
        EPaymentRequestStatus.FailedPaidIn,
        EPaymentRequestStatus.FailedTransfer,
        EPaymentRequestStatus.FailedPayout,
        EPaymentRequestStatus.FailedKyc,
        EPaymentRequestStatus.Failed,
      ];

    default:
      return [];
  }
};
